if (!window.__BublTestimonialsSliderInitialized) {
  window.__BublTestimonialsSliderInitialized = true;

  (() => {
  const SELECTORS = {
    slider: '[data-testimonials-slider]',
    track: '[data-testimonials-track]',
    slide: '[data-testimonials-slide]',
    pagination: '[data-testimonials-pagination]'
  };

  const MOBILE_QUERY = '(max-width: 749px)';
  const instanceMap = new WeakMap();
  const animationMap = new WeakMap();
  const supportsMatchMedia =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  const reduceMotionQuery = supportsMatchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;
  const supportsIntersectionObserver =
    typeof window !== 'undefined' && 'IntersectionObserver' in window;

  class TestimonialsSlider {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector(SELECTORS.track);
      this.slides = Array.from(root.querySelectorAll(SELECTORS.slide));
      this.pagination = root.querySelector(SELECTORS.pagination);
      this.mql = window.matchMedia(MOBILE_QUERY);
      this.dots = [];
      this.activeIndex = -1;
      this.isEnabled = false;
      this.ticking = false;
      this.isValid = this.track && this.pagination && this.slides.length > 1;

      if (!this.isValid) {
        if (this.pagination) {
          this.pagination.setAttribute('hidden', 'hidden');
        }
        return;
      }

      this.handleScroll = this.handleScroll.bind(this);
      this.handleMediaChange = this.handleMediaChange.bind(this);

      this.init();
    }

    init() {
      this.createDots();
      this.handleMediaChange(this.mql);
      if (this.mql.addEventListener) {
        this.mql.addEventListener('change', this.handleMediaChange);
      } else if (this.mql.addListener) {
        this.mql.addListener(this.handleMediaChange);
      }
    }

    createDots() {
      if (this.dots.length) {
        return;
      }

      this.pagination.innerHTML = '';

      this.slides.forEach((slide, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'testimonials__dot';
        button.setAttribute('aria-label', `Go to testimonial ${index + 1}`);
        const handler = () => this.goToSlide(index);
        button.addEventListener('click', handler);

        this.dots.push({ button, handler });
        this.pagination.appendChild(button);
      });
    }

    enable() {
      if (this.isEnabled) {
        return;
      }

      this.isEnabled = true;
      this.pagination.removeAttribute('hidden');
      const initialIndex = this.activeIndex >= 0 ? this.activeIndex : 0;
      this.goToSlide(initialIndex);
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
    }

    disable() {
      if (!this.isEnabled) {
        return;
      }

      this.isEnabled = false;
      this.track.removeEventListener('scroll', this.handleScroll);
      this.pagination.setAttribute('hidden', 'hidden');
    }

    handleMediaChange(event) {
      const matches =
        event && typeof event.matches === 'boolean' ? event.matches : this.mql.matches;

      if (matches) {
        this.enable();
      } else {
        this.disable();
      }
    }

    handleScroll() {
      if (!this.isEnabled || this.ticking) {
        return;
      }

      this.ticking = true;
      requestAnimationFrame(() => {
        this.updateActive();
        this.ticking = false;
      });
    }

    updateActive() {
      if (!this.isEnabled) {
        return;
      }

      const trackRect = this.track.getBoundingClientRect();
      const trackCenter = trackRect.left + trackRect.width / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const slideCenter = rect.left + rect.width / 2;
        const distance = Math.abs(slideCenter - trackCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      this.setActive(closestIndex);
    }

    setActive(index) {
      if (index < 0 || index >= this.dots.length) {
        return;
      }

      this.activeIndex = index;

      this.dots.forEach(({ button }, dotIndex) => {
        const isActive = dotIndex === index;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    goToSlide(index) {
      const slide = this.slides[index];

      if (!slide) {
        return;
      }

      const trackRect = this.track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const offset =
        slideRect.left -
        trackRect.left +
        this.track.scrollLeft -
        (trackRect.width - slideRect.width) / 2;

      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const target = Math.max(0, Math.min(offset, maxScroll));

      this.track.scrollTo({ left: target, behavior: 'smooth' });
      this.setActive(index);
    }

    destroy() {
      if (!this.isValid) {
        return;
      }

      this.disable();
      if (this.mql.removeEventListener) {
        this.mql.removeEventListener('change', this.handleMediaChange);
      } else if (this.mql.removeListener) {
        this.mql.removeListener(this.handleMediaChange);
      }
      this.dots.forEach(({ button, handler }) => {
        button.removeEventListener('click', handler);
      });
      this.dots = [];
      if (this.pagination) {
        this.pagination.innerHTML = '';
      }
    }
  }

  const initSliders = (scope = document) => {
    scope.querySelectorAll(SELECTORS.slider).forEach((slider) => {
      if (!instanceMap.has(slider)) {
        const instance = new TestimonialsSlider(slider);
        if (instance.isValid) {
          instanceMap.set(slider, instance);
        }
      }
    });
  };

  const destroySliders = (scope = document) => {
    scope.querySelectorAll(SELECTORS.slider).forEach((slider) => {
      const instance = instanceMap.get(slider);
      if (instance) {
        instance.destroy();
        instanceMap.delete(slider);
      }
    });
  };

  const initLoadAnimations = (scope = document) => {
    scope.querySelectorAll(SELECTORS.track).forEach((track) => {
      if (animationMap.has(track)) {
        return;
      }

      const items = Array.from(track.querySelectorAll(SELECTORS.slide));

      if (!items.length) {
        return;
      }

      items.forEach((item, index) => {
        item.style.setProperty('--testimonial-stagger-index', index.toString());
      });

      const prefersReducedMotion = !!(reduceMotionQuery && reduceMotionQuery.matches);

      const reveal = () => {
        track.classList.add('testimonials__list--visible');
        animationMap.set(track, { observer: null });
      };

      if (prefersReducedMotion || !supportsIntersectionObserver) {
        reveal();
        return;
      }

      track.classList.add('testimonials__list--animate');

      const observer = new IntersectionObserver(
        (entries, obs) => {
          const isVisible = entries.some((entry) => entry.isIntersecting);

          if (isVisible) {
            track.classList.add('testimonials__list--visible');
            obs.disconnect();
            animationMap.set(track, { observer: null });
          }
        },
        {
          root: null,
          threshold: 0.2,
          rootMargin: '0px 0px -10% 0px'
        }
      );

      observer.observe(track);
      animationMap.set(track, { observer });
    });
  };

  const destroyLoadAnimations = (scope = document) => {
    scope.querySelectorAll(SELECTORS.track).forEach((track) => {
      const payload = animationMap.get(track);
      if (payload && payload.observer) {
        payload.observer.disconnect();
      }
      track.classList.remove('testimonials__list--animate', 'testimonials__list--visible');
      animationMap.delete(track);
    });
  };

  const initTestimonials = (scope = document) => {
    initSliders(scope);
    initLoadAnimations(scope);
  };

  const destroyTestimonials = (scope = document) => {
    destroySliders(scope);
    destroyLoadAnimations(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initTestimonials());
  } else {
    initTestimonials();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initTestimonials(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyTestimonials(event.target);
  });
  })();
}
